import React from 'react';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeManyUsers } from '@/lib/collections/users';
import type { UsersResponse } from '@/lib/collections/users';

function abbreviateAddress(addr: string): string {
  if (!addr || addr.length < 10) return addr;
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

const RANK_COLORS = ['#fbbf24', '#94a3b8', '#cd7f32'];

export const Leaderboard: React.FC = () => {
  const { data: users } = useRealtimeData<UsersResponse[]>(
    subscribeManyUsers,
    true,
    'order by totalPoints desc limit 10'
  );

  const topUsers = users ?? [];

  return (
    <div
      className="rounded-3xl overflow-hidden"
      style={{
        background: 'rgba(26,10,46,0.7)',
        border: '1px solid rgba(167,139,250,0.25)',
      }}
    >
      <div
        className="px-5 py-3 flex items-center gap-2 font-bold"
        style={{
          background: 'linear-gradient(135deg, rgba(255,61,168,0.15), rgba(167,139,250,0.1))',
          borderBottom: '1px solid rgba(167,139,250,0.2)',
          color: '#fbbf24',
        }}
      >
        <span>🏆</span>
        <span>Leaderboard</span>
        <span className="ml-auto text-xs font-normal" style={{ color: '#a78bfa' }}>
          Top 10
        </span>
      </div>

      {topUsers.length === 0 ? (
        <div className="text-center py-8 text-sm" style={{ color: '#a78bfa' }}>
          🏁 No players yet — be the first!
        </div>
      ) : (
        <div className="divide-y divide-white/5">
          {topUsers.map((user, i) => (
            <div key={user.id ?? user.userAddress} className="flex items-center gap-3 px-5 py-2.5">
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                style={{
                  background: i < 3 ? `${RANK_COLORS[i]}33` : 'rgba(255,255,255,0.08)',
                  border: `1.5px solid ${i < 3 ? RANK_COLORS[i] : 'rgba(255,255,255,0.15)'}`,
                  color: i < 3 ? RANK_COLORS[i] : 'rgba(255,255,255,0.5)',
                }}
              >
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate text-white/90">
                  {abbreviateAddress(user.userAddress)}
                </div>
                <div className="text-xs" style={{ color: '#a78bfa' }}>
                  {user.totalWins ?? 0} wins · {user.totalRoundsPlayed ?? 0} rounds
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-bold text-sm" style={{ color: '#fbbf24' }}>
                  {(user.totalPoints ?? 0).toLocaleString()} pts
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
